/**
 * High-level execution APIs — executeTask / executeWorkflow
 *
 * Encapsulates the common boilerplate shared across all CLI consumers:
 *   1. Adapter creation
 *   2. DSL context loading (registries)
 *   3. Progress sink setup
 *   4. Task/workflow invocation with retry policy
 *
 * CLI consumers only need to provide:
 *   - domain-specific context (user_request)
 *   - adapter name + options
 *   - DSL blob (resolvedDsl from codegen)
 */

import { resolve } from "node:path";
import { createAdapter, type CreateAdapterOptions } from "./adapter-factory.js";
import { loadDslContext, type DslContext } from "./dsl-context.js";
import { createProgressSink, type ProgressSink } from "./progress-sink.js";
import { runTask, type TaskRunResult, type SdkAdapter } from "./task-runner.js";
import { runWorkflow, type WorkflowResult, type WorkflowInvocation, type HandoffInput, type StepCompleteEvent, type StepResult, type WorkflowRegistries, type DelegateStep, type AdapterOrFactory } from "./workflow-runner.js";
import type { TaskContext } from "./task-runner.js";
import type { ModelMappingConfig } from "./model-resolver.js";

// ---------------------------------------------------------------------------
// Shared options
// ---------------------------------------------------------------------------

export interface ExecuteOptions {
  adapter: string;
  model?: string;
  dsl: Record<string, unknown>;
  projectDslPath?: string;
  logFile?: string;
  adapterOptions?: Partial<CreateAdapterOptions>;
  maxFollowUps?: number;
  maxRetries?: number;
}

// ---------------------------------------------------------------------------
// executeTask
// ---------------------------------------------------------------------------

export interface TaskHookContext {
  adapter: SdkAdapter;
  registries: WorkflowRegistries;
  taskId: string;
}

export interface ExecuteTaskOptions extends ExecuteOptions {
  request: string;
  hooks?: {
    beforeTask?: (ctx: TaskHookContext) => Promise<void>;
    afterTask?: (result: TaskRunResult) => Promise<void>;
  };
}

export async function executeTask(
  taskId: string,
  options: ExecuteTaskOptions,
): Promise<TaskRunResult> {
  const adapter = await createAdapter(options.adapter, {
    model: options.model,
    ...options.adapterOptions,
  });

  const ctx = await loadDslContext({
    embeddedDsl: options.dsl,
    projectDslPath: options.projectDslPath,
    requiredEntities: { tasks: [taskId] },
  });

  const progressSink = createProgressSink(
    options.logFile
      ? { stderr: true, file: resolve(options.logFile), naming: "single" }
      : { stderr: true },
  );

  try {
    if (options.hooks?.beforeTask) {
      await options.hooks.beforeTask({ adapter, registries: ctx.registries, taskId });
    }

    const result = await runTask(adapter, taskId, { user_request: options.request }, {
      maxFollowUps: options.maxFollowUps ?? 2,
      maxRetries: options.maxRetries ?? 0,
      progressOutput: progressSink,
      ...ctx.registries,
    });

    if (options.hooks?.afterTask) {
      await options.hooks.afterTask(result);
    }

    return result;
  } finally {
    progressSink.close();
  }
}

// ---------------------------------------------------------------------------
// executeWorkflow
// ---------------------------------------------------------------------------

export interface WorkflowHookContext {
  adapter: SdkAdapter;
  registries: WorkflowRegistries;
  workflowId: string;
}

export interface ExecuteWorkflowOptions extends ExecuteOptions {
  request?: string;
  handoff?: HandoffInput<string>;
  context?: {
    cwd?: string;
    environment?: "local" | "ci" | "remote";
    artifacts?: Record<string, string>;
    variables?: Record<string, unknown>;
  };
  autoApproveGates?: boolean;
  hooks?: {
    beforeWorkflow?: (ctx: WorkflowHookContext) => Promise<void>;
    afterWorkflow?: (result: WorkflowResult) => Promise<void>;
    onStepComplete?: (event: StepCompleteEvent) => void;
    onGate?: (gateKind: string, description: string, stepResults: StepResult[]) => Promise<boolean>;
    onOptionalStep?: (step: DelegateStep, context: TaskContext) => Promise<boolean>;
  };
}

export async function executeWorkflow(
  workflowId: string,
  options: ExecuteWorkflowOptions,
): Promise<WorkflowResult> {
  const adapter = await createAdapter(options.adapter, {
    model: options.model,
    ...options.adapterOptions,
  });

  const ctx = await loadDslContext({
    embeddedDsl: options.dsl,
    projectDslPath: options.projectDslPath,
    requiredEntities: { workflows: [workflowId] },
  });

  const progressSink = createProgressSink(
    options.logFile
      ? { stderr: true, file: resolve(options.logFile), naming: "single" }
      : { stderr: true },
  );

  try {
    if (options.hooks?.beforeWorkflow) {
      await options.hooks.beforeWorkflow({ adapter, registries: ctx.registries, workflowId });
    }

    const invocation: WorkflowInvocation = {
      workflow: workflowId,
      user_request: options.request,
      handoff: options.handoff,
      progressOutput: progressSink,
      runtime: {
        maxFollowUps: options.maxFollowUps ?? 2,
        maxRetries: options.maxRetries ?? 0,
      },
      hooks: {
        onStepComplete: options.hooks?.onStepComplete,
        onGate: options.hooks?.onGate ?? (options.autoApproveGates ? async () => true : undefined),
        onOptionalStep: options.hooks?.onOptionalStep,
      },
      context: options.context,
    };

    const result = await runWorkflow(adapter, invocation, ctx.registries);

    if (options.hooks?.afterWorkflow) {
      await options.hooks.afterWorkflow(result);
    }

    return result;
  } finally {
    progressSink.close();
  }
}
